import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Typography,
  Grid,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tooltip,
  TablePagination,
  Card,
  CardContent,
  InputAdornment,
  Divider,
  CircularProgress,
} from '@mui/material';
import {
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Visibility as VisibilityIcon,
  Replay as RetryIcon,
  Cancel as CancelIcon,
  Send as SendIcon,
  Delete as DeleteIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Schedule as ScheduleIcon,
  HourglassEmpty as PendingIcon,
  FilterListOff as FilterListOffIcon,
} from '@mui/icons-material';
import {
  EmailQueue,
  EmailStatus,
  EmailQueueStats,
  EmailQueueFilter,
} from '../../../types/emailQueue';
import { emailQueueAPI } from '../../../services/api';
import { getModuleTableTheme } from '../../../theme/tableTheme';
import AlertDialog from '../../../components/AlertDialog';
import useAlert from '../../../hooks/useAlert';
import { extractErrorMessage } from '../../../utils/errorHandler';
import ColumnFilter from '../../../components/ColumnFilter';
import { useColumnFilters } from '../../../hooks/useColumnFilters';

const CONFIG_COLOR = '#1976D2';

const EMAIL_HISTORY_FILTER_SCHEMA = {
  to_email: { type: 'text',      param: 'to_email' },
  subject:  { type: 'text',      param: 'subject' },
  module:   { type: 'select',    param: 'module' },
  status:   { type: 'select',    param: 'status' },
  date:     { type: 'dateRange', paramFrom: 'date_from', paramTo: 'date_to' },
} as const;

const statusConfig: Record<EmailStatus, { label: string; color: 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning'; icon: React.ReactElement }> = {
  pending: { label: 'En attente', color: 'warning', icon: <PendingIcon fontSize="small" /> },
  sending: { label: 'En cours', color: 'info', icon: <ScheduleIcon fontSize="small" /> },
  sent: { label: 'Envoyé', color: 'success', icon: <CheckCircleIcon fontSize="small" /> },
  failed: { label: 'Échec', color: 'error', icon: <ErrorIcon fontSize="small" /> },
  cancelled: { label: 'Annulé', color: 'default', icon: <CancelIcon fontSize="small" /> },
};

const moduleLabels: Record<string, string> = {
  commercial: 'Commercial',
  hr: 'Ressources Humaines',
  stock: 'Stock',
  system: 'Système',
};

const EmailHistoryManagement: React.FC = () => {
  const [emails, setEmails] = useState<EmailQueue[]>([]);
  const [modules, setModules] = useState<string[]>([]);
  const [stats, setStats] = useState<EmailQueueStats | null>(null);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState<number | null>(null);

  // Free-text search across to_email/subject/to_name
  const [searchFilter, setSearchFilter] = useState('');

  // Preview dialog
  const [previewOpen, setPreviewOpen] = useState(false);
  const [selectedEmail, setSelectedEmail] = useState<EmailQueue | null>(null);

  const { alertState, showError, showSuccess, showConfirm, hideAlert } = useAlert();

  const { filters: colFilters, update, reset, hasActive, toQueryParams } = useColumnFilters(
    EMAIL_HISTORY_FILTER_SCHEMA,
    { onChange: () => setPage(0) }
  );

  const fetchEmails = useCallback(async () => {
    try {
      setLoading(true);
      const filters: EmailQueueFilter & Record<string, any> = {
        page: page + 1,
        page_size: pageSize,
        ...toQueryParams(),
      };

      if (searchFilter) filters.search = searchFilter;

      const response = await emailQueueAPI.getAll(filters);
      setEmails(response.items);
      setTotal(response.total);
    } catch (error) {
      showError('Erreur lors du chargement de l\'historique des emails');
      console.error('Error fetching email history:', error);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize, searchFilter, colFilters, showError]);

  const fetchStats = async () => {
    try {
      const data = await emailQueueAPI.getStats();
      setStats(data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const fetchModules = async () => {
    try {
      const data = await emailQueueAPI.getModules();
      setModules(data);
    } catch (error) {
      console.error('Error fetching modules:', error);
    }
  };

  useEffect(() => {
    fetchEmails();
    fetchStats();
    fetchModules();
  }, [fetchEmails]);

  const handleRetry = async (email: EmailQueue) => {
    showConfirm(
      `Réessayer l'envoi de l'email à ${email.to_email} ?`,
      async () => {
        try {
          setActionLoading(email.id);
          const result = await emailQueueAPI.retry(email.id);
          if (result.success) {
            showSuccess(result.message);
            fetchEmails();
            fetchStats();
          } else {
            showError(result.message);
          }
        } catch (err: any) {
          showError(extractErrorMessage(err));
        } finally {
          setActionLoading(null);
        }
      },
      {
        title: 'Réessayer l\'envoi',
        confirmText: 'Réessayer',
        cancelText: 'Annuler',
      }
    );
  };

  const handleSendNow = async (email: EmailQueue) => {
    showConfirm(
      `Envoyer immédiatement l'email à ${email.to_email} ?`,
      async () => {
        try {
          setActionLoading(email.id);
          const result = await emailQueueAPI.sendNow(email.id);
          if (result.success) {
            showSuccess(result.message);
            fetchEmails();
            fetchStats();
          } else {
            showError(result.message);
          }
        } catch (err: any) {
          showError(extractErrorMessage(err));
        } finally {
          setActionLoading(null);
        }
      },
      {
        title: 'Envoyer maintenant',
        confirmText: 'Envoyer',
        cancelText: 'Annuler',
      }
    );
  };

  const handleCancel = async (email: EmailQueue) => {
    showConfirm(
      `Annuler l'envoi de l'email à ${email.to_email} ?`,
      async () => {
        try {
          setActionLoading(email.id);
          const result = await emailQueueAPI.cancel(email.id);
          if (result.success) {
            showSuccess(result.message);
            fetchEmails();
            fetchStats();
          } else {
            showError(result.message);
          }
        } catch (err: any) {
          showError(extractErrorMessage(err));
        } finally {
          setActionLoading(null);
        }
      },
      {
        title: 'Annuler l\'envoi',
        confirmText: 'Annuler l\'email',
        cancelText: 'Retour',
      }
    );
  };

  const handleDelete = async (email: EmailQueue) => {
    showConfirm(
      `Supprimer définitivement cet email de l'historique ?`,
      async () => {
        try {
          setActionLoading(email.id);
          await emailQueueAPI.delete(email.id);
          showSuccess('Email supprimé de l\'historique');
          fetchEmails();
          fetchStats();
        } catch (err: any) {
          showError(extractErrorMessage(err));
        } finally {
          setActionLoading(null);
        }
      },
      {
        title: 'Supprimer l\'email',
        confirmText: 'Supprimer',
        cancelText: 'Annuler',
      }
    );
  };

  const handlePreview = (email: EmailQueue) => {
    setSelectedEmail(email);
    setPreviewOpen(true);
  };

  const handleClosePreview = () => {
    setPreviewOpen(false);
    setSelectedEmail(null);
  };

  const handleClearFilters = () => {
    setSearchFilter('');
    reset();
    setPage(0);
  };

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setPageSize(parseInt(event.target.value, 10));
    setPage(0);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const renderStatsCards = () => {
    if (!stats) return null;

    const cards = [
      { label: 'Total', value: stats.total, color: '#1976D2' },
      { label: 'En attente', value: stats.pending, color: '#FF9800' },
      { label: 'En cours', value: stats.sending, color: '#2196F3' },
      { label: 'Envoyés', value: stats.sent, color: '#4CAF50' },
      { label: 'Échecs', value: stats.failed, color: '#F44336' },
      { label: "Envoyés aujourd'hui", value: stats.sent_today, color: '#9C27B0' },
    ];

    return (
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {cards.map((card) => (
          <Grid item xs={6} sm={4} md={2} key={card.label}>
            <Card variant="outlined">
              <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
                <Typography variant="h4" sx={{ fontWeight: 600, color: card.color }}>
                  {card.value}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {card.label}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    );
  };

  const hasActiveFilters = searchFilter || hasActive();

  return (
      <Box>
        <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h5">Historique des Emails</Typography>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={() => {
              fetchEmails();
              fetchStats();
            }}
            disabled={loading}
          >
            Actualiser
          </Button>
        </Box>

        {renderStatsCards()}

        {/* Filters */}
        <Paper sx={{ p: 2, mb: 3 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={8} md={6}>
              <TextField
                fullWidth
                variant="outlined"
                size="small"
                placeholder="Rechercher par email, sujet, destinataire..."
                value={searchFilter}
                onChange={(e) => setSearchFilter(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon color="action" />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item>
              {hasActiveFilters && (
                <Button
                  size="small"
                  startIcon={<FilterListOffIcon />}
                  onClick={handleClearFilters}
                  sx={{ color: 'text.secondary' }}
                >
                  Effacer les filtres
                </Button>
              )}
            </Grid>
          </Grid>
        </Paper>

        {/* Table */}
        <Box sx={getModuleTableTheme('configuration')}>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      Destinataire
                      <ColumnFilter type="text" value={colFilters.to_email} onChange={(v) => update('to_email', v)} label="email" color={CONFIG_COLOR} />
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      Sujet
                      <ColumnFilter type="text" value={colFilters.subject} onChange={(v) => update('subject', v)} label="sujet" color={CONFIG_COLOR} />
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      Module
                      <ColumnFilter
                        type="select"
                        value={colFilters.module}
                        onChange={(v) => update('module', v)}
                        label="module"
                        color={CONFIG_COLOR}
                        options={modules.map((m) => ({ value: m, label: moduleLabels[m] || m }))}
                      />
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      Statut
                      <ColumnFilter
                        type="select"
                        value={colFilters.status}
                        onChange={(v) => update('status', v)}
                        label="statut"
                        color={CONFIG_COLOR}
                        options={Object.entries(statusConfig).map(([key, c]) => ({ value: key, label: c.label }))}
                      />
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      Date
                      <ColumnFilter type="dateRange" value={colFilters.date} onChange={(v) => update('date', v)} label="date" color={CONFIG_COLOR} />
                    </Box>
                  </TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                      <CircularProgress size={40} />
                    </TableCell>
                  </TableRow>
                ) : emails.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                      Aucun email trouvé
                    </TableCell>
                  </TableRow>
                ) : (
                  emails.map((email) => {
                    const config = statusConfig[email.status];
                    return (
                      <TableRow key={email.id}>
                        <TableCell>
                          <Typography variant="body2" fontWeight={500}>
                            {email.to_email}
                          </Typography>
                          {email.to_name && (
                            <Typography variant="caption" color="text.secondary">
                              {email.to_name}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Typography
                            variant="body2"
                            sx={{
                              maxWidth: 250,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {email.subject}
                          </Typography>
                          {email.event && (
                            <Typography variant="caption" color="text.secondary">
                              {email.event.name}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          {email.module ? (
                            <Chip
                              label={moduleLabels[email.module] || email.module}
                              size="small"
                              variant="outlined"
                            />
                          ) : (
                            <Typography variant="caption" color="text.secondary">
                              -
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Chip
                            icon={config.icon}
                            label={config.label}
                            color={config.color}
                            size="small"
                          />
                          {email.status === 'failed' && email.retry_count > 0 && (
                            <Typography variant="caption" display="block" color="error">
                              {email.retry_count}/{email.max_retries} tentatives
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {formatDate(email.created_at)}
                          </Typography>
                          {email.sent_at && (
                            <Typography variant="caption" color="success.main" display="block">
                              Envoyé: {formatDate(email.sent_at)}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell align="right">
                          {actionLoading === email.id ? (
                            <CircularProgress size={24} />
                          ) : (
                            <>
                              <Tooltip title="Voir le détail">
                                <IconButton
                                  onClick={() => handlePreview(email)}
                                  color="primary"
                                  size="small"
                                >
                                  <VisibilityIcon />
                                </IconButton>
                              </Tooltip>
                              {email.status === 'pending' && (
                                <>
                                  <Tooltip title="Envoyer maintenant">
                                    <IconButton
                                      onClick={() => handleSendNow(email)}
                                      color="success"
                                      size="small"
                                    >
                                      <SendIcon />
                                    </IconButton>
                                  </Tooltip>
                                  <Tooltip title="Annuler">
                                    <IconButton
                                      onClick={() => handleCancel(email)}
                                      color="warning"
                                      size="small"
                                    >
                                      <CancelIcon />
                                    </IconButton>
                                  </Tooltip>
                                </>
                              )}
                              {email.status === 'failed' && email.retry_count < email.max_retries && (
                                <Tooltip title="Réessayer">
                                  <IconButton
                                    onClick={() => handleRetry(email)}
                                    color="info"
                                    size="small"
                                  >
                                    <RetryIcon />
                                  </IconButton>
                                </Tooltip>
                              )}
                              {(email.status === 'sent' || email.status === 'cancelled' || email.status === 'failed') && (
                                <Tooltip title="Supprimer">
                                  <IconButton
                                    onClick={() => handleDelete(email)}
                                    color="error"
                                    size="small"
                                  >
                                    <DeleteIcon />
                                  </IconButton>
                                </Tooltip>
                              )}
                            </>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
            <TablePagination
              component="div"
              count={total}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={pageSize}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[10, 25, 50, 100]}
              labelRowsPerPage="Lignes par page:"
              labelDisplayedRows={({ from, to, count }) =>
                `${from}-${to} sur ${count !== -1 ? count : `plus de ${to}`}`
              }
            />
          </TableContainer>
        </Box>

        {/* Preview Dialog */}
        <Dialog open={previewOpen} onClose={handleClosePreview} maxWidth="md" fullWidth>
          <DialogTitle>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              Détail de l'Email
              {selectedEmail && (
                <Chip
                  icon={statusConfig[selectedEmail.status].icon}
                  label={statusConfig[selectedEmail.status].label}
                  color={statusConfig[selectedEmail.status].color}
                  size="small"
                />
              )}
            </Box>
          </DialogTitle>
          <DialogContent dividers>
            {selectedEmail && (
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Destinataire
                  </Typography>
                  <Typography variant="body1">
                    {selectedEmail.to_name ? `${selectedEmail.to_name} <${selectedEmail.to_email}>` : selectedEmail.to_email}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Date de création
                  </Typography>
                  <Typography variant="body1">
                    {formatDate(selectedEmail.created_at)}
                  </Typography>
                </Grid>
                {selectedEmail.cc_emails && selectedEmail.cc_emails.length > 0 && (
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="text.secondary">
                      CC
                    </Typography>
                    <Typography variant="body1">
                      {selectedEmail.cc_emails.join(', ')}
                    </Typography>
                  </Grid>
                )}
                {selectedEmail.bcc_emails && selectedEmail.bcc_emails.length > 0 && (
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="text.secondary">
                      BCC
                    </Typography>
                    <Typography variant="body1">
                      {selectedEmail.bcc_emails.join(', ')}
                    </Typography>
                  </Grid>
                )}
                {selectedEmail.sent_at && (
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Date d'envoi
                    </Typography>
                    <Typography variant="body1" color="success.main">
                      {formatDate(selectedEmail.sent_at)}
                    </Typography>
                  </Grid>
                )}
                {selectedEmail.smtp_setting && (
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Serveur SMTP
                    </Typography>
                    <Typography variant="body1">
                      {selectedEmail.smtp_setting.name} ({selectedEmail.smtp_setting.from_email})
                    </Typography>
                  </Grid>
                )}
                {selectedEmail.event && (
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Événement
                    </Typography>
                    <Typography variant="body1">
                      {selectedEmail.event.name}
                    </Typography>
                  </Grid>
                )}
                {selectedEmail.module && (
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Module
                    </Typography>
                    <Chip
                      label={moduleLabels[selectedEmail.module] || selectedEmail.module}
                      size="small"
                      variant="outlined"
                    />
                  </Grid>
                )}
                {selectedEmail.error_message && (
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" color="error">
                      Message d'erreur
                    </Typography>
                    <Paper variant="outlined" sx={{ p: 1, bgcolor: 'error.lighter' }}>
                      <Typography variant="body2" color="error.dark">
                        {selectedEmail.error_message}
                      </Typography>
                    </Paper>
                  </Grid>
                )}
                <Grid item xs={12}>
                  <Divider sx={{ my: 1 }} />
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Sujet
                  </Typography>
                  <Typography variant="body1" fontWeight={500}>
                    {selectedEmail.subject}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                    Corps du message
                  </Typography>
                  <Paper
                    variant="outlined"
                    sx={{
                      p: 2,
                      maxHeight: 300,
                      overflow: 'auto',
                      bgcolor: 'grey.50',
                    }}
                  >
                    <div
                      dangerouslySetInnerHTML={{ __html: selectedEmail.body }}
                      style={{ fontSize: '14px' }}
                    />
                  </Paper>
                </Grid>
                {selectedEmail.attachments && selectedEmail.attachments.length > 0 && (
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Pièces jointes
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                      {selectedEmail.attachments.map((attachment, index) => (
                        <Chip
                          key={index}
                          label={attachment}
                          size="small"
                          variant="outlined"
                        />
                      ))}
                    </Box>
                  </Grid>
                )}
                {selectedEmail.created_by && (
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Créé par
                    </Typography>
                    <Typography variant="body2">
                      {selectedEmail.created_by.full_name || selectedEmail.created_by.email}
                    </Typography>
                  </Grid>
                )}
              </Grid>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClosePreview}>Fermer</Button>
            {selectedEmail && selectedEmail.status === 'pending' && (
              <>
                <Button
                  variant="contained"
                  color="success"
                  startIcon={<SendIcon />}
                  onClick={() => {
                    handleClosePreview();
                    handleSendNow(selectedEmail);
                  }}
                >
                  Envoyer maintenant
                </Button>
                <Button
                  variant="outlined"
                  color="warning"
                  startIcon={<CancelIcon />}
                  onClick={() => {
                    handleClosePreview();
                    handleCancel(selectedEmail);
                  }}
                >
                  Annuler
                </Button>
              </>
            )}
            {selectedEmail && selectedEmail.status === 'failed' && selectedEmail.retry_count < selectedEmail.max_retries && (
              <Button
                variant="contained"
                color="info"
                startIcon={<RetryIcon />}
                onClick={() => {
                  handleClosePreview();
                  handleRetry(selectedEmail);
                }}
              >
                Réessayer
              </Button>
            )}
          </DialogActions>
        </Dialog>

        <AlertDialog
          open={alertState.open}
          type={alertState.type}
          title={alertState.title}
          message={alertState.message}
          onClose={hideAlert}
          onConfirm={alertState.onConfirm}
          confirmText={alertState.confirmText}
          cancelText={alertState.cancelText}
          showCancel={alertState.showCancel}
        />
      </Box>
  );
};

export default EmailHistoryManagement;
